<!--
  A passkey link (/k/:token): someone who lost their passkey or moved to another browser makes a
  new passkey for their account here and is signed in. Their other passkeys keep working.
-->
<script lang="ts">
  import * as P from "../settings/passkeys.ts";
  import { fill, shows } from "../settings/index.ts";
  import { api, ApiError, NetworkError } from "../lib/api.ts";
  import { app } from "../lib/store.svelte.ts";
  import { member } from "../lib/member.svelte.ts";
  import { router } from "../lib/router.svelte.ts";
  import { passkeyError, passkeysSupported, registerPasskey } from "../lib/auth.ts";

  let { token }: { token: string } = $props();

  let info = $state<{ name: string; username: string | null } | null>(null);
  let error = $state("");
  let busy = $state(false);
  let problem = $state("");
  const supported = passkeysSupported();

  $effect(() => {
    void api<{ name: string; username: string | null }>(`/api/passkey-link/${token}`)
      .then((r) => (info = r))
      .catch((e) => (error = e instanceof ApiError ? e.message : P.PAGE_OFFLINE));
  });

  const otherSignedIn = $derived(app.signedIn === true && info !== null && app.userName !== info.name);
  const isOwner = $derived(app.signedIn === true && app.role === "owner");

  async function create() {
    busy = true;
    problem = "";
    try {
      if (otherSignedIn) {
        await app.signOut();
        member.clear();
      }
      await registerPasskey({ passkeyToken: token });
      await app.refreshAccount();
      if (app.role === "member") await member.refreshGroups();
      app.toast(P.PAGE_DONE_TOAST);
      router.go("/");
    } catch (e) {
      problem = e instanceof NetworkError ? P.PAGE_OFFLINE : passkeyError(e);
    } finally {
      busy = false;
    }
  }

  async function copyLink() {
    try {
      await navigator.clipboard.writeText(location.href);
      app.toast(P.PAGE_COPIED_TOAST);
    } catch {
      app.toast(P.PAGE_COPY_FAILED, "error");
    }
  }
</script>

<div class="screen">
  <div class="card">
    {#if error}
      <h1>{P.PAGE_BROKEN_TITLE}</h1>
      <p class="muted">{error}{shows(P.PAGE_BROKEN_TEXT) ? ` ${P.PAGE_BROKEN_TEXT}` : ""}</p>
    {:else if !info || !app.ready}
      <p class="muted">{P.PAGE_LOADING}</p>
    {:else}
      {#if shows(P.PAGE_EYEBROW)}<p class="eyebrow">{P.PAGE_EYEBROW}</p>{/if}
      <h1>{fill(P.PAGE_TITLE, { name: info.name })}</h1>
      {#if shows(P.PAGE_TEXT)}<p class="muted">{P.PAGE_TEXT}</p>{/if}
      {#if otherSignedIn && shows(P.PAGE_OTHER_SIGNED_IN)}
        <p class="note">{fill(P.PAGE_OTHER_SIGNED_IN, { me: app.userName, name: info.name })}</p>
      {/if}
      <div class="row">
        {#if isOwner && otherSignedIn}
          <button class="btn primary" onclick={copyLink}>{P.PAGE_COPY_BUTTON}</button>
          <button class="btn" disabled={busy || !supported} onclick={create}>{P.PAGE_BUTTON}</button>
        {:else}
          <button class="btn primary" disabled={busy || !supported} onclick={create}>{P.PAGE_BUTTON}</button>
        {/if}
      </div>
      {#if !supported}<p class="owes">{P.PAGE_UNSUPPORTED}</p>{/if}
      {#if problem}<p class="owes" role="alert">{problem}</p>{/if}
    {/if}
  </div>
</div>

<style>
  .screen {
    min-height: 100vh;
    display: grid;
    place-items: center;
    padding: 24px var(--gutter);
  }

  .card {
    width: min(440px, 100%);
    display: grid;
    gap: 14px;
    padding: 28px;
    border-radius: var(--r-lg);
    background: var(--surface);
    border: 1px solid var(--rule);
  }

  .eyebrow {
    margin: 0;
    color: var(--ink-2);
    font-weight: 600;
  }

  h1 {
    margin: 0;
    font-size: var(--t-2xl);
  }

  p {
    margin: 0;
  }

  .note {
    font-size: var(--t-sm);
    color: var(--ink-2);
  }

  .row {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
  }
</style>
